/**
 * spin-game.js
 * 3D-Karussell mit GAMERS ONLY Booster Packs + API-Calls für das Shopify Lootbox-Gewinnspiel.
 * Vanilla JS, keine externen Abhängigkeiten.
 * Backend-Zugriff über den Shopify App Proxy (/apps/spin/*): Shopify hängt die
 * signierte ID des eingeloggten Kunden an — einmal ziehen pro Kundenkonto und Tag.
 * Das Karussell ist rein optisch: Der Gewinn wird beim Loslassen bzw. Klick auf dem
 * Server gezogen, die Animation läuft parallel aus und deckt ihn nur auf.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Konfiguration aus Liquid injiziertem window.__spinGame
  // ---------------------------------------------------------------------------
  const CONFIG = window.__spinGame || {};
  const MIN_ORDER_VALUE = CONFIG.minOrderValue || 25;
  // Shopify.routes.root berücksichtigt Sprach-Präfixe (z. B. /en/)
  const PROXY_BASE = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'apps/spin';

  function store(key, value) {
    try { localStorage.setItem(key, value); } catch (_) {}
  }

  // Mindestbestellwert für das Warenkorb-Script (spin-game-cart.js) auf anderen Seiten hinterlegen
  store('spin_game_min_order_value', String(MIN_ORDER_VALUE));

  const RARITY = {
    common:    { label: 'Gewöhnlich',   level: 1, pulses: 1 },
    uncommon:  { label: 'Ungewöhnlich', level: 2, pulses: 1 },
    rare:      { label: 'Selten',       level: 3, pulses: 2 },
    epic:      { label: 'Episch',       level: 4, pulses: 2 },
    legendary: { label: 'Legendär',     level: 5, pulses: 3 },
  };
  const PACK_COUNT = 10;
  const STEP = 360 / PACK_COUNT;
  const SPIN_LABEL = 'Jetzt Pack öffnen';
  const IDLE_HINT = 'Wisch das Karussell an – oder tippe auf „Jetzt Pack öffnen".';

  // ---------------------------------------------------------------------------
  // DOM Elemente
  // ---------------------------------------------------------------------------
  const root = document.getElementById('spin-game');
  if (!root) return;

  const $ = id => document.getElementById(id);
  const stateLoading   = $('state-loading');
  const stateEnded     = $('state-ended');
  const stateEndedMsg  = $('state-ended-message');
  const statePlayed    = $('state-played');
  const alreadyWonInfo = $('already-won-info');
  const btnClaimReturn = $('btn-claim-return');
  const deckStage      = $('deck-stage');
  const respinNotice   = $('respin-notice');
  const carousel       = $('go-carousel');
  const ring           = $('carousel-ring');
  const marker         = $('carousel-marker');
  const hero           = $('carousel-hero');
  const heroPack       = $('hero-pack');
  const cardWrap       = $('card-wrap');
  const drawCard       = $('draw-card');
  const fxCanvas       = $('deck-fx');
  const hint           = $('carousel-hint');
  const btnSpin        = $('btn-spin');
  const resultPanel    = $('result-panel');
  const resultTitle    = $('result-title');
  const resultRarity   = $('result-rarity');
  const resultLimited  = $('result-limited');
  const btnClaim       = $('btn-claim');
  const playedLabel    = statePlayed ? statePlayed.querySelector('.spin-game__played-label') : null;

  let wonProduct = null;
  let wonVariantId = null; // Fallback wenn wonProduct noch nicht geladen
  let mustRespin = false;  // Gewinn beim Claim vergriffen → Button lädt neu zum erneuten Ziehen
  let canDraw = false;     // erst nach erfolgreicher Berechtigungsprüfung

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  const mod = (n, m) => ((n % m) + m) % m;
  const rarityOf = product => (RARITY[product && product.rarityTier] ? product.rarityTier : 'common');
  const rarityColor = tier => getComputedStyle(root).getPropertyValue('--rarity-' + tier).trim();

  // ---------------------------------------------------------------------------
  // API Calls — über den App Proxy auf der Shop-Domain (Kunden-ID setzt Shopify)
  // ---------------------------------------------------------------------------
  async function apiFetch(path, options = {}) {
    const response = await fetch(`${PROXY_BASE}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      credentials: 'same-origin',
    });
    const json = await response.json();
    if (!response.ok) {
      throw Object.assign(new Error(json.error || 'API Fehler'), { status: response.status, data: json });
    }
    return json;
  }

  async function checkEligibility() {
    return apiFetch('/eligibility');
  }

  // POST immer mit Body: Der App Proxy lehnt POSTs ohne Content-Length mit 411 ab
  async function postSpin() {
    return apiFetch('/spin', { method: 'POST', body: '{}' });
  }

  async function postClaim() {
    return apiFetch('/claim', { method: 'POST', body: '{}' });
  }

  // ---------------------------------------------------------------------------
  // Packs aufbauen: Kopf und Körper tragen dieselbe Grafik, damit der Kopf abreißen kann
  // ---------------------------------------------------------------------------
  const PACK_ART = '<div class="go-pack__art"><div class="go-pack__logo"></div>'
    + '<p class="go-pack__brand">GAMERS ONLY</p><p class="go-pack__title">Booster Pack</p>'
    + '<p class="go-pack__sub">1 KARTE</p><div class="go-pack__tear"></div></div>';
  const PACK_HTML = `<div class="go-pack__top">${PACK_ART}</div><div class="go-pack__body">${PACK_ART}</div>`;

  const packs = [];
  for (let i = 0; i < PACK_COUNT; i++) {
    const pack = document.createElement('div');
    pack.className = 'go-pack';
    pack.setAttribute('aria-hidden', 'true');
    pack.innerHTML = PACK_HTML;
    ring.appendChild(pack);
    packs.push(pack);
  }
  heroPack.innerHTML = PACK_HTML;
  const heroTop = heroPack.querySelector('.go-pack__top');
  const heroBody = heroPack.querySelector('.go-pack__body');

  // Gezackte Ränder: oben und unten gecrimpt (Folienpack), dazwischen die Abrisskante.
  // Kopf und Körper teilen sich exakt dieselbe Abrisslinie → geschlossen wirkt das Pack nahtlos.
  function crimpClips(w, h) {
    const tooth = Math.max(4, w * 0.035);
    const teeth = 14;
    const topH = h * 0.17;
    const zig = (y0, dir) => Array.from({ length: teeth + 1 }, (_, i) => `${(i / teeth) * 100}% ${y0 + (i % 2 ? dir * tooth : 0)}px`);
    const tear = Array.from({ length: 11 }, (_, i) => [(i / 10) * 100, (Math.sin(i * 2.3) * 0.5 + 0.5) * tooth]);
    const top = [...zig(0, 1), ...tear.slice().reverse().map(([x, y]) => `${x}% ${topH + y}px`)];
    const body = [...tear.map(([x, y]) => `${x}% ${y}px`), ...zig(h - topH, -1).reverse()];
    return { top: `polygon(${top.join(',')})`, body: `polygon(${body.join(',')})`, topHeight: topH + tooth };
  }

  // Maße immer von einem Ring-Pack: das Hero-Pack ist beim Messen oft noch versteckt
  const packW = () => packs[0].offsetWidth || 130;
  const packH = () => packs[0].offsetHeight || 208;
  // Auf schmalen Bildschirmen die aufgedeckte Karte etwas größer zeigen, damit sie lesbar bleibt
  const cardScale = () => (packW() < 120 ? 1.5 : 1.25);
  const heroLift = () => `translateY(${-packH() * 0.06}px) scale(1.12)`;
  const finalCardTransform = () => `translateY(${-packH() * 0.05}px) scale(${cardScale()})`;

  let radius = 280;
  function layout() {
    if (deckStage.hidden) return;
    const w = packW();
    const h = packH();
    radius = (w * 1.2 * PACK_COUNT) / (2 * Math.PI);
    packs.forEach((pack, i) => { pack.style.transform = `rotateY(${i * STEP}deg) translateZ(${radius}px)`; });
    const clips = crimpClips(w, h);
    root.querySelectorAll('.go-pack__top').forEach(el => { el.style.clipPath = clips.top; el.style.height = clips.topHeight + 'px'; });
    root.querySelectorAll('.go-pack__body').forEach(el => { el.style.clipPath = clips.body; });
    if (state === 'done') {
      hero.style.transform = heroLift();
      cardWrap.style.transform = finalCardTransform();
    }
    render();
    sizeCanvas();
  }

  // ---------------------------------------------------------------------------
  // Ring-Zustand: idle → coasting → revealing → done (Gäste: idle ↔ gliding)
  // ---------------------------------------------------------------------------
  let angle = 0;
  let state = 'idle';
  let lastFront = -1;
  let dragging = false;
  let heroIndex = -1; // Ring-Pack, an dessen Stelle das Hero-Pack steht

  function render() {
    ring.style.transform = `translateZ(${-radius}px) rotateY(${-angle}deg)`;
    // Abgewandte Packs ausblenden und nach Tiefe sortieren — nicht jeder Browser
    // setzt backface-visibility bzw. die 3D-Reihenfolge zuverlässig um
    packs.forEach((pack, i) => {
      const facing = Math.cos(((i * STEP - angle) * Math.PI) / 180);
      pack.style.visibility = facing < -0.05 || i === heroIndex ? 'hidden' : '';
      pack.style.zIndex = String(Math.round((facing + 1) * 50));
    });
    const front = mod(Math.round(angle / STEP), PACK_COUNT);
    if (front !== lastFront) {
      if (lastFront >= 0 && (dragging || state === 'coasting' || state === 'gliding')) sfx.tick();
      lastFront = front;
    }
  }

  // Ein Zähler pro Fahrt: eine neue Fahrt oder ein Abbruch beendet die laufende
  let motionId = 0;
  function animateAngle(from, to, duration, ease) {
    const id = ++motionId;
    return new Promise(resolve => {
      const t0 = performance.now();
      function frame(now) {
        if (id !== motionId) { resolve(); return; }
        const t = Math.min(1, (now - t0) / duration);
        angle = from + (to - from) * ease(t);
        render();
        if (t < 1) requestAnimationFrame(frame); else resolve();
      }
      requestAnimationFrame(frame);
    });
  }
  const stopMotion = () => { motionId++; };

  // Ruhezustand: das Karussell dreht sich ganz langsam, bis jemand es anfasst
  let idleRunning = false;
  function startIdle() {
    if (reducedMotion || idleRunning) return;
    idleRunning = true;
    let last = performance.now();
    requestAnimationFrame(function idle(now) {
      if (state !== 'idle') { idleRunning = false; return; }
      if (!dragging && !deckStage.hidden) {
        angle += Math.min(now - last, 50) * 0.004;
        render();
      }
      last = now;
      requestAnimationFrame(idle);
    });
  }

  function setHint(text) {
    if (!hint) return;
    hint.textContent = text;
    hint.hidden = !text;
  }

  // ---------------------------------------------------------------------------
  // Wischen per Maus/Finger
  // ---------------------------------------------------------------------------
  let startX = 0;
  let startAngle = 0;
  let samples = [];
  const degPerPx = () => STEP / (packW() * 1.1);

  carousel.addEventListener('pointerdown', e => {
    if (state !== 'idle' && state !== 'gliding') return;
    if (state === 'gliding') { stopMotion(); state = 'idle'; startIdle(); }
    dragging = true;
    startX = e.clientX;
    startAngle = angle;
    samples = [{ x: e.clientX, t: performance.now() }];
    try { carousel.setPointerCapture(e.pointerId); } catch (_) {}
    carousel.classList.add('is-dragging');
    sfx.unlock();
  });

  carousel.addEventListener('pointermove', e => {
    if (!dragging) return;
    angle = startAngle - (e.clientX - startX) * degPerPx();
    samples.push({ x: e.clientX, t: performance.now() });
    if (samples.length > 6) samples.shift();
    render();
  });

  carousel.addEventListener('pointerup', e => {
    if (!dragging) return;
    dragging = false;
    carousel.classList.remove('is-dragging');
    const moved = Math.abs(e.clientX - startX);
    if (moved < 8) return; // nur angetippt: das Karussell dreht im Leerlauf weiter
    const a = samples[0];
    const b = samples[samples.length - 1];
    const v = b && a && b.t > a.t ? -((b.x - a.x) / (b.t - a.t)) * degPerPx() : 0; // Grad pro ms
    const dir = Math.sign(v) || Math.sign(startX - e.clientX) || 1;
    if (canDraw) startDraw(dir, Math.abs(v));
    else glide(dir, Math.abs(v));
  });

  // Browser übernimmt die Geste (z. B. senkrechtes Scrollen) → nichts auslösen
  carousel.addEventListener('pointercancel', () => {
    dragging = false;
    carousel.classList.remove('is-dragging');
  });

  carousel.addEventListener('keydown', e => {
    if ((e.key === 'Enter' || e.key === ' ') && canDraw && state === 'idle') {
      e.preventDefault();
      startDraw(1, 0.55 + Math.random() * 0.35);
    }
  });

  // Gäste dürfen das Karussell anwischen, es läuft aber nur kurz aus — ohne Ziehung
  function glide(dir, speed) {
    if (reducedMotion) return;
    state = 'gliding';
    const dist = Math.min(360, Math.max(STEP, speed * 500));
    const target = Math.round((angle + dir * dist) / STEP) * STEP;
    animateAngle(angle, target, 1400, easeOutCubic).then(() => {
      if (state === 'gliding') { state = 'idle'; startIdle(); }
    });
  }

  // ---------------------------------------------------------------------------
  // Sound — synthetisch über Web Audio, startet erst nach einer Interaktion
  // ---------------------------------------------------------------------------
  const sfx = (function () {
    let ctx = null;
    function ac() {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    }
    function noise(dur, f0, f1, vol) {
      if (!ctx) return;
      try {
        const c = ac();
        const len = Math.floor(c.sampleRate * dur);
        const buf = c.createBuffer(1, len, c.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
        const src = c.createBufferSource();
        src.buffer = buf;
        const bp = c.createBiquadFilter();
        bp.type = 'bandpass';
        bp.Q.value = 1.1;
        bp.frequency.setValueAtTime(f0, c.currentTime);
        bp.frequency.exponentialRampToValueAtTime(f1, c.currentTime + dur);
        const gain = c.createGain();
        gain.gain.setValueAtTime(vol, c.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
        src.connect(bp); bp.connect(gain); gain.connect(c.destination);
        src.start();
      } catch (_) {}
    }
    function tone(freq, dur, type, vol, delay, slide) {
      if (!ctx) return;
      try {
        const c = ac();
        const t = c.currentTime + (delay || 0);
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = type || 'sine';
        osc.frequency.setValueAtTime(freq, t);
        if (slide) osc.frequency.exponentialRampToValueAtTime(slide, t + dur);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(vol, t + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        osc.connect(gain); gain.connect(c.destination);
        osc.start(t); osc.stop(t + dur + 0.05);
      } catch (_) {}
    }
    return {
      unlock: () => { try { ac(); } catch (_) {} },
      tick:   () => noise(0.025, 3600, 2000, 0.13),
      whoosh: () => noise(0.5, 300, 2400, 0.18),
      rip:    () => { noise(0.32, 5200, 900, 0.32); noise(0.18, 2500, 600, 0.2); },
      beep:   level => tone(220 * Math.pow(2, (level * 3) / 12), 0.15, 'square', 0.05),
      pulse:  level => tone(110 * Math.pow(2, level / 6), 0.36, 'sawtooth', 0.04, 0, 55 * Math.pow(2, level / 6)),
      reveal: level => {
        const base = [262, 294, 330, 392, 440][level - 1];
        [1, 1.25, 1.5, 2].forEach((m, i) => tone(base * m, 0.55, 'triangle', 0.1, i * 0.07));
        if (level === 5) tone(65, 0.9, 'sine', 0.22, 0, 40);
      },
    };
  })();

  // ---------------------------------------------------------------------------
  // Partikel beim Aufdecken
  // ---------------------------------------------------------------------------
  const fx = fxCanvas.getContext('2d');
  let particles = [];
  let fxFrame = 0;
  let fxW = 0;
  let fxH = 0;

  function sizeCanvas() {
    const rect = carousel.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    fxW = rect.width;
    fxH = rect.height;
    fxCanvas.width = Math.round(fxW * dpr);
    fxCanvas.height = Math.round(fxH * dpr);
    fx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function burst(tier) {
    const level = RARITY[tier].level;
    const cx = fxW / 2;
    const cy = fxH / 2 - packH() * 0.05;
    const colors = [rarityColor(tier), rarityColor(tier), '#ffffff', level >= 4 ? rarityColor(tier) : '#00e5ff'];
    const count = [40, 55, 80, 110, 170][level - 1];
    for (let i = 0; i < count; i++) {
      const dir = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * (3 + level * 1.2);
      particles.push({
        x: cx, y: cy,
        vx: Math.cos(dir) * speed, vy: Math.sin(dir) * speed - 1.5,
        w: 2 + Math.random() * 4, h: 1 + Math.random() * 2.5,
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.3,
        life: 1, decay: 0.011 + Math.random() * 0.012,
        color: colors[(Math.random() * colors.length) | 0],
      });
    }
    if (!fxFrame) fxFrame = requestAnimationFrame(tickParticles);
  }

  function tickParticles() {
    fx.clearRect(0, 0, fxW, fxH);
    particles = particles.filter(p => p.life > 0);
    for (const p of particles) {
      p.vy += 0.07; p.vx *= 0.986;
      p.x += p.vx; p.y += p.vy; p.rot += p.vr; p.life -= p.decay;
      fx.globalAlpha = Math.max(0, p.life);
      fx.fillStyle = p.color;
      fx.save();
      fx.translate(p.x, p.y);
      fx.rotate(p.rot);
      fx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      fx.restore();
    }
    fx.globalAlpha = 1;
    fxFrame = particles.length ? requestAnimationFrame(tickParticles) : 0;
  }

  // ---------------------------------------------------------------------------
  // Karten-Vorderseite befüllen
  // ---------------------------------------------------------------------------
  // Shopify-CDN-Bilder in passender Größe laden
  function sizedImage(url, width) {
    if (!url) return '';
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (_) {
      return url;
    }
  }

  function preloadImage(src) {
    return new Promise(resolve => {
      if (!src) { resolve(); return; }
      const img = new Image();
      img.onload = img.onerror = () => resolve();
      img.src = src;
      setTimeout(resolve, 2500); // nicht ewig auf ein langsames Bild warten
    });
  }

  function splitTitle(title) {
    const idx = (title || '').indexOf(' – ');
    return idx === -1 ? [title || '', ''] : [title.slice(0, idx), title.slice(idx + 3)];
  }

  function limitedText(product) {
    if (!product || !product.limit) return '';
    const remaining = product.remaining === null || product.remaining === undefined ? product.limit : product.remaining;
    return `Limitiert: Nur die ersten ${product.limit} Bestellungen erhalten diesen Gewinn – noch ${remaining} verfügbar.`;
  }

  // Gibt die (bereits angefragte) Bild-URL zurück, damit der Aufruf vorladen kann
  function prepareCard(product) {
    const tier = rarityOf(product);
    const [name, variant] = splitTitle(product.title);
    drawCard.style.setProperty('--rc', rarityColor(tier));
    $('card-tier').textContent = RARITY[tier].label;
    $('card-product').textContent = name;
    $('card-variant').textContent = variant;
    $('card-limited').textContent = product.limit
      ? `Noch ${product.remaining ?? product.limit} von ${product.limit}`
      : '';
    const img = $('card-img');
    const src = sizedImage(product.image, 480);
    img.alt = product.title || '';
    img.hidden = !src;
    if (src) img.src = src;
    return src;
  }

  // ---------------------------------------------------------------------------
  // Ziehung: Karussell läuft aus, parallel zieht der Server den Gewinn
  // ---------------------------------------------------------------------------
  function coast(dir, speed) {
    if (reducedMotion) {
      return animateAngle(angle, Math.round(angle / STEP + dir) * STEP, 300, t => t);
    }
    const v0 = Math.max(speed, 0.35);                         // Mindestschwung (Grad/ms)
    const dist = Math.min(1800, Math.max(540, v0 * 1100));   // mind. 1,5 Umdrehungen
    const target = Math.round((angle + dir * dist) / STEP) * STEP;
    const duration = Math.min(5200, Math.max(2600, (3 * Math.abs(target - angle)) / v0));
    return animateAngle(angle, target, duration, easeOutCubic);
  }

  async function startDraw(dir, speed) {
    if (!canDraw || state !== 'idle') return;
    state = 'coasting';
    carousel.classList.add('is-locked');
    if (btnSpin) {
      btnSpin.disabled = true;
      btnSpin.textContent = 'Pack wird gezogen…';
    }
    setHint('Das Karussell läuft aus …');
    sfx.unlock();
    sfx.whoosh();

    const spin = postSpin();
    const coasting = coast(dir, speed);

    let product;
    try {
      product = (await spin).winning_product;
    } catch (err) {
      stopMotion();
      console.error('Ziehung fehlgeschlagen:', err);
      if (err.status === 409) {
        // Heute schon gezogen (z. B. in einem anderen Tab) → Gewinn per Neuladen anzeigen
        window.location.reload();
      } else if (err.status === 401) {
        showLoginRequired();
      } else {
        showSpinError(err.message);
        resetToIdle();
      }
      return;
    }

    if (respinNotice) respinNotice.hidden = true;
    const imageReady = preloadImage(prepareCard(product));
    await coasting;
    await imageReady;
    await reveal(product);
    showResult(product);
  }

  function resetToIdle() {
    state = 'idle';
    carousel.classList.remove('is-locked');
    if (btnSpin) {
      btnSpin.disabled = false;
      btnSpin.textContent = SPIN_LABEL;
    }
    setHint(IDLE_HINT);
    startIdle();
  }

  // ---------------------------------------------------------------------------
  // Aufdecken: Pack nach vorn, Seltenheit, Aufreißen, Karte
  // ---------------------------------------------------------------------------
  // Das vordere Ring-Pack wird durch das Hero-Pack an exakt derselben Stelle ersetzt
  function bringPackForward() {
    heroIndex = mod(Math.round(angle / STEP), PACK_COUNT);
    render();
    hero.hidden = false;
    ring.classList.add('is-done');
    marker.style.opacity = '0';
  }

  // Endzustand als feste Styles setzen (nach der Animation, nach Neuladen oder bei "Bewegung reduzieren").
  // Erst die Styles, dann die Animationen beenden — sie enden im selben Zustand, nichts springt.
  function showRevealedCard() {
    bringPackForward();
    hero.style.transform = heroLift();
    heroTop.style.opacity = '0';
    heroBody.style.opacity = '0';
    cardWrap.style.transform = finalCardTransform();
    drawCard.style.transform = 'rotateY(180deg)';
    [hero, heroPack, heroTop, heroBody, cardWrap, drawCard].forEach(el => el.getAnimations().forEach(a => a.cancel()));
    carousel.classList.add('is-locked');
    state = 'done';
  }

  async function reveal(product) {
    state = 'revealing';
    const tier = rarityOf(product);
    const level = RARITY[tier].level;
    const h = packH();
    setHint('Dein Pack wird geöffnet …');

    if (reducedMotion) {
      showRevealedCard();
      sfx.reveal(level);
      return;
    }

    bringPackForward();
    await hero.animate(
      [{ transform: 'translateY(0) scale(1)' }, { transform: `translateY(${-h * 0.06}px) scale(1.12)` }],
      { duration: 450, easing: 'cubic-bezier(.3,.7,.2,1)', fill: 'forwards' }
    ).finished;

    // Spannung ohne Leuchten: das Pack rüttelt Stufe für Stufe stärker, je seltener der Gewinn
    const steps = [240, 270, 320, 420, 560];
    const shake = (strength, duration) => heroPack.animate([
      { transform: 'translate(0, 0) rotate(0deg)' },
      { transform: `translate(${-strength}px, ${-strength / 2}px) rotate(${-strength / 2}deg)`, offset: 0.25 },
      { transform: `translate(${strength}px, 0) rotate(${strength / 2}deg)`, offset: 0.5 },
      { transform: `translate(${-strength / 2}px, ${-strength / 3}px) rotate(${-strength / 4}deg)`, offset: 0.75 },
      { transform: 'translate(0, 0) rotate(0deg)' },
    ], { duration });
    for (let l = 1; l <= level; l++) {
      shake(1.5 + l, 160);
      sfx.beep(l);
      await wait(steps[l - 1]);
    }
    for (let k = 0; k < RARITY[tier].pulses; k++) {
      shake(3 + level, 320);
      sfx.pulse(level);
      await wait(420);
    }

    // Aufreißen: der Kopf fliegt weg
    sfx.rip();
    heroTop.animate(
      [{ transform: 'translate(0, 0) rotate(0deg)', opacity: 1 }, { transform: `translate(${-h * 0.45}px, ${-h * 0.55}px) rotate(-38deg)`, opacity: 0 }],
      { duration: 620, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' }
    );
    await wait(260);

    // Karte steigt aus dem Pack, das Pack fällt weg
    await cardWrap.animate(
      [{ transform: 'translateY(0)' }, { transform: `translateY(${-h * 0.5}px)` }],
      { duration: 700, easing: 'cubic-bezier(.3,.6,.2,1)', fill: 'forwards' }
    ).finished;
    heroBody.animate(
      [{ transform: 'translateY(0)', opacity: 1 }, { transform: `translateY(${h * 0.35}px)`, opacity: 0 }],
      { duration: 450, easing: 'cubic-bezier(.5,0,.8,.4)', fill: 'forwards' }
    );

    // Karte in die Mitte und umdrehen
    sfx.reveal(level);
    cardWrap.animate(
      [{ transform: `translateY(${-h * 0.5}px) scale(1)` }, { transform: finalCardTransform() }],
      { duration: 650, easing: 'cubic-bezier(.3,.7,.2,1)', fill: 'forwards' }
    );
    setTimeout(() => burst(tier), 380);
    await drawCard.animate(
      [{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(196deg)', offset: 0.75 }, { transform: 'rotateY(180deg)' }],
      { duration: 760, easing: 'cubic-bezier(.5,0,.3,1)', fill: 'forwards' }
    ).finished;

    // Endzustand als feste Styles übernehmen, damit er Größenänderungen übersteht
    showRevealedCard();
  }

  // ---------------------------------------------------------------------------
  // Ergebnis anzeigen
  // ---------------------------------------------------------------------------
  function showResult(product, silent) {
    wonProduct = product;
    const tier = rarityOf(product);
    const [name, variant] = splitTitle(product.title);

    resultTitle.textContent = name;
    if (variant) {
      const sv = document.createElement('span');
      sv.className = 'result-title__variant';
      sv.textContent = variant;
      resultTitle.appendChild(sv);
    }
    resultRarity.textContent = RARITY[tier].label;
    resultRarity.dataset.rarity = tier;
    resultLimited.textContent = limitedText(product);
    resultLimited.hidden = !product.limit;

    // Widget-Daten für seitenübergreifende Nutzung speichern
    store('spin_game_won_variant_id', String(product.id));
    store('spin_game_won_title', product.title || '');
    store('spin_game_won_image', product.image || '');

    if (btnSpin) btnSpin.hidden = true;
    setHint('');
    resultPanel.hidden = false;
    if (!silent) resultPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ---------------------------------------------------------------------------
  // Hinweise
  // ---------------------------------------------------------------------------
  // Fehler beim Ziehen (z. B. Kampagne beendet, keine Gewinne verfügbar) über dem Karussell anzeigen
  function showSpinError(message) {
    if (!respinNotice || !message) return;
    respinNotice.textContent = message;
    respinNotice.hidden = false;
  }

  // Shop zeigt den Kunden als eingeloggt, der App Proxy aber nicht → Hinweis statt Neuladen (keine Schleife)
  function showLoginRequired() {
    stateLoading.hidden = true;
    deckStage.hidden = true;
    if (stateEndedMsg) {
      stateEndedMsg.textContent = 'Bitte melde dich erneut an, um mitzuspielen. ';
      if (CONFIG.loginUrl) {
        const link = document.createElement('a');
        link.href = CONFIG.loginUrl;
        link.textContent = 'Zum Login';
        stateEndedMsg.appendChild(link);
      }
    }
    stateEnded.hidden = false;
  }

  function showClaimHint(message) {
    let el = $('claim-min-hint');
    if (!el) {
      el = document.createElement('p');
      el.id = 'claim-min-hint';
      el.className = 'spin-game__limited-hint';
      const anchor = (btnClaimReturn && !btnClaimReturn.hidden) ? btnClaimReturn : btnClaim;
      if (anchor) anchor.insertAdjacentElement('afterend', el);
    }
    el.textContent = message;
  }

  // ---------------------------------------------------------------------------
  // Warenkorb
  // ---------------------------------------------------------------------------
  // _spin_win trägt das Datum der Ziehung: Der Gewinn ist nur an diesem Tag einlösbar,
  // danach entfernt spin-game-cart.js ihn und die Checkout-Regel sperrt ihn.
  async function addToCart(variantId, playDate) {
    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { '_spin_win': playDate },
      }),
    });
    if (!addRes.ok) {
      let msg = 'Warenkorb-Fehler';
      try {
        const errJson = await addRes.json();
        if (errJson.description) msg = errJson.description;
      } catch (_) {}
      throw new Error(msg);
    }
    // Für die Warenkorb-Hinweise (spin-game-cart.js). Den Rabatt selbst prüft die
    // Shopify Function gegen den Gewinn im Kundenkonto.
    store('spin_game_won_variant_id', String(variantId));
  }

  // Rebuy Smart Cart öffnen — mehrere Fallback-Strategien
  function openRebuyCart() {
    if (window.Rebuy && window.Rebuy.SmartCart) {
      const api = window.Rebuy.SmartCart;
      if (typeof api.show   === 'function') { api.show();   return; }
      if (typeof api.open   === 'function') { api.open();   return; }
      if (typeof api.toggle === 'function') { api.toggle(); return; }
    }
    document.dispatchEvent(new CustomEvent('rebuy:cart:open'));
    const cartBtn = document.querySelector(
      '[data-rebuy-cart-toggle], .rebuy-cart__toggle, #rebuy-cart-toggle, [data-rebuy-cart-open]'
    );
    if (cartBtn) { cartBtn.click(); return; }
    // Letzter Ausweg: regulärer Warenkorb
    window.location.href = '/cart';
  }

  async function claim(button, idleText) {
    if (mustRespin) { window.location.reload(); return; }
    const variantId = (wonProduct && wonProduct.id) || wonVariantId;
    if (!variantId) return;

    button.disabled = true;
    button.textContent = 'Wird verarbeitet…';
    try {
      const claimed = await postClaim();
      await addToCart(variantId, claimed.playDate);
      button.textContent = '✓ Im Warenkorb!';
      button.style.cursor = 'default';
      openRebuyCart();
    } catch (err) {
      console.error('Claim Fehler:', err);
      button.disabled = false;
      if (err.data && err.data.soldOut) {
        // Limitierter Gewinn inzwischen vergriffen → Seite neu laden und erneut ziehen
        mustRespin = true;
        button.textContent = 'Nochmal ziehen';
      } else if (err.data && err.data.expired) {
        // Gewinn von gestern (Seite über Mitternacht offen) → neu laden, heute neu ziehen
        mustRespin = true;
        button.textContent = 'Heute neu ziehen';
      } else {
        button.textContent = idleText;
      }
      showClaimHint(err.message || 'Fehler beim Hinzufügen zum Warenkorb.');
    }
  }

  // ---------------------------------------------------------------------------
  // Initialisierung
  // ---------------------------------------------------------------------------
  function showStage() {
    deckStage.hidden = false;
    layout();
  }

  async function init() {
    // Gäste: Karussell zeigen, statt "Jetzt Pack öffnen" den Login-Button (in der Section gerendert)
    if (!CONFIG.loggedIn) {
      // Nach dem Login auf exakt diese URL zurück (inkl. Parameter wie Theme-Vorschau oder UTM);
      // Liquid kennt nur den Pfad. return_to muss relativ sein.
      const btnLogin = $('btn-login');
      if (btnLogin) {
        btnLogin.href = '/customer_authentication/login?return_to='
          + encodeURIComponent(window.location.pathname + window.location.search);
      }
      stateLoading.hidden = true;
      showStage();
      startIdle();
      return;
    }

    stateLoading.hidden = false;

    try {
      const eligibility = await checkEligibility();
      stateLoading.hidden = true;

      if (eligibility.loginRequired) {
        showLoginRequired();
        return;
      }

      if (!eligibility.eligible) {
        if (eligibility.wonProduct) {
          wonVariantId = eligibility.wonProduct.id;

          if (eligibility.redeemed) {
            if (playedLabel) playedLabel.textContent = eligibility.reason;
            statePlayed.hidden = false;
          } else if (eligibility.wonProduct.title) {
            // Aufgedeckte Karte + Ergebnis zeigen (identisch mit der Ansicht nach dem Ziehen)
            prepareCard(eligibility.wonProduct);
            showStage();
            showRevealedCard();
            showResult(eligibility.wonProduct, true);
          } else {
            // Fallback falls das Produkt nicht mehr im Sortiment ist
            statePlayed.hidden = false;
            alreadyWonInfo.hidden = false;
            btnClaimReturn.hidden = false;
          }
        } else {
          if (stateEndedMsg) stateEndedMsg.textContent = eligibility.reason || '';
          stateEnded.hidden = false;
        }
        return;
      }

      // Limitierter Gewinn vergriffen, bevor bestellt wurde → einmal neu ziehen
      if (eligibility.respin && respinNotice) {
        const title = eligibility.soldOutProduct && eligibility.soldOutProduct.title;
        respinNotice.textContent = (title ? `„${title}" ist` : 'Dein Gewinn ist')
          + ' leider vergriffen – du darfst noch einmal ziehen!';
        respinNotice.hidden = false;
      }

      canDraw = true;
      showStage();
      startIdle();
    } catch (err) {
      stateLoading.hidden = true;
      console.error('Init-Fehler:', err);
      if (stateEndedMsg) stateEndedMsg.textContent = 'Verbindungsfehler. Bitte Seite neu laden.';
      stateEnded.hidden = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------
  if (btnSpin) {
    btnSpin.addEventListener('click', () => startDraw(1, 0.55 + Math.random() * 0.35));
  }

  if (btnClaim) {
    btnClaim.addEventListener('click', () => claim(btnClaim, 'Kostenlos in den Warenkorb'));
  }

  if (btnClaimReturn) {
    btnClaimReturn.addEventListener('click', () => claim(btnClaimReturn, 'Kostenlos in den Warenkorb'));
  }

  window.addEventListener('resize', layout);

  init();

})();
